import React, { useState } from 'react';
import { Outlet, Navigate, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { cn } from '../../lib/utils';
import { Toaster } from '../ui/sonner';
import { 
  LayoutDashboard,
  MapPin,
  Zap,
  Activity,
  Users,
  CreditCard,
  Banknote,
  FileText,
  PieChart,
  BarChart3,
  LogOut,
  ChevronLeft,
  ChevronRight,
  Menu,
  X
} from 'lucide-react';

const moderatorNavItems = [
  { path: '/moderator/dashboard', label: 'Paneli', icon: LayoutDashboard },
  { path: '/moderator/sites', label: 'Stacionet', icon: MapPin },
  { path: '/moderator/chargers', label: 'Karikuesit', icon: Zap },
  { path: '/moderator/sessions', label: 'Seancat', icon: Activity },
  { path: '/moderator/clients', label: 'Klientët', icon: Users },
  { path: '/moderator/rfid-cards', label: 'Kartat RFID', icon: CreditCard },
  { path: '/moderator/cash-topup', label: 'Mbushje Cash', icon: Banknote },
  { path: '/moderator/invoices', label: 'Faturat', icon: FileText },
  { path: '/moderator/analytics', label: 'Analiza', icon: PieChart },
  { path: '/moderator/reports', label: 'Raportet', icon: BarChart3 },
];

// Sidebar content component extracted outside
const SidebarContent = ({ 
  collapsed, 
  isMobile, 
  user, 
  onLogout, 
  onCollapse, 
  onMobileClose 
}) => (
  <div className="flex flex-col h-full">
    {/* Logo */}
    <div className={cn(
      "flex items-center p-4 border-b border-white/10",
      collapsed && !isMobile ? "justify-center" : "gap-3"
    )}>
      <img 
        src="/logo-dark.png" 
        alt="AlbMove" 
        className={cn("h-8 w-auto", collapsed && !isMobile && "h-6")}
      />
      {(!collapsed || isMobile) && (
        <div className="flex flex-col">
          <span className="text-sm font-semibold text-white">AlbMove</span>
          <span className="text-xs text-emerald-400">Moderator</span>
        </div>
      )}
    </div>

    {/* User Info */}
    {(!collapsed || isMobile) && (
      <div className="p-4 border-b border-white/10">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-emerald-500/10 flex items-center justify-center">
            <Users className="w-5 h-5 text-emerald-500" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-white truncate">{user?.full_name}</p>
            <p className="text-xs text-zinc-500 truncate">{user?.email}</p>
          </div>
        </div>
      </div>
    )}

    {/* Navigation */}
    <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
      {moderatorNavItems.map((item) => (
        <NavLink
          key={item.path}
          to={item.path}
          onClick={() => isMobile && onMobileClose?.()}
          className={({ isActive }) => cn(
            "flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors",
            collapsed && !isMobile ? "justify-center" : "",
            isActive 
              ? "bg-emerald-500/10 text-emerald-400" 
              : "text-zinc-400 hover:bg-white/5 hover:text-white"
          )}
        >
          <item.icon className="w-5 h-5 flex-shrink-0" />
          {(!collapsed || isMobile) && (
            <span className="text-sm font-medium">{item.label}</span>
          )}
        </NavLink>
      ))}
    </nav>

    {/* Logout Button */}
    <div className="p-3 border-t border-white/10">
      <button
        onClick={onLogout}
        className={cn(
          "flex items-center gap-3 w-full px-3 py-2.5 rounded-lg text-red-400 hover:bg-red-500/10 transition-colors",
          collapsed && !isMobile ? "justify-center" : ""
        )}
      >
        <LogOut className="w-5 h-5" />
        {(!collapsed || isMobile) && <span className="text-sm">Dilni</span>}
      </button>
    </div>

    {/* Collapse Button - Desktop only */}
    {!isMobile && onCollapse && (
      <button
        onClick={onCollapse}
        className="absolute -right-3 top-20 w-6 h-6 bg-zinc-800 border border-white/10 rounded-full flex items-center justify-center text-zinc-400 hover:text-white transition-colors"
      >
        {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
      </button>
    )}
  </div>
);

const ModeratorSidebar = ({ collapsed, setCollapsed }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <>
      {/* Desktop Sidebar */}
      <aside className={cn(
        "hidden lg:flex fixed left-0 top-0 h-screen bg-zinc-900/95 border-r border-white/10 flex-col z-40 transition-all duration-300",
        collapsed ? "w-20" : "w-64"
      )}>
        <SidebarContent 
          collapsed={collapsed}
          isMobile={false}
          user={user}
          onLogout={handleLogout}
          onCollapse={() => setCollapsed(!collapsed)}
        />
      </aside>

      {/* Mobile Menu Button */}
      <button
        onClick={() => setMobileOpen(true)}
        className="lg:hidden fixed top-4 left-4 z-50 p-2 bg-zinc-900 border border-white/10 rounded-lg text-white"
      >
        <Menu className="w-5 h-5" />
      </button>

      {/* Mobile Sidebar Overlay */}
      {mobileOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Mobile Sidebar */}
      <aside className={cn(
        "lg:hidden fixed left-0 top-0 h-screen w-72 bg-zinc-900 border-r border-white/10 z-50 transform transition-transform duration-300",
        mobileOpen ? "translate-x-0" : "-translate-x-full"
      )}>
        <button
          onClick={() => setMobileOpen(false)}
          className="absolute top-4 right-4 p-2 text-zinc-400 hover:text-white"
        >
          <X className="w-5 h-5" />
        </button>
        <SidebarContent 
          collapsed={false}
          isMobile={true}
          user={user}
          onLogout={handleLogout}
          onMobileClose={() => setMobileOpen(false)}
        />
      </aside>
    </>
  );
};

export const ModeratorLayout = () => {
  const { isAuthenticated, loading, user } = useAuth();
  const { isDark } = useTheme();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Only allow MODERATOR role
  if (user?.role !== 'MODERATOR') {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className={cn('min-h-screen bg-background transition-colors duration-300', isDark ? 'dark' : 'light')}>
      <ModeratorSidebar 
        collapsed={sidebarCollapsed} 
        setCollapsed={setSidebarCollapsed}
      />
      
      <div className={cn(
        "min-h-screen transition-all duration-300",
        sidebarCollapsed ? "lg:ml-20" : "lg:ml-64"
      )}>
        <main className="p-6 pt-16 lg:pt-6 animate-fade-in">
          <Outlet />
        </main>
      </div>
      
      <Toaster richColors position="top-right" />
    </div>
  );
};

export default ModeratorLayout;
